// ── The default Agreement and POA dates (report 90208a98) ────────────────────────────────────────
// The reporter: "In the property form always use today's date as the default for the agreement dates and POA dates.
// Unless, today is not a working day (like Saturday, Sunday, or a public holiday) then, use the next working day."
// Only the server knows the holidays, so it says the day (`day`, defaultDay in server/src/services/propertyForm.ts) and
// the screen does the rest, on two boxes: agreement.date and poa.date. Each keeps a marker beside it, `dateDefault`:
//   · the day it was filled with: the box still holds a default. While it still equals its marker and the form is New it
//     goes on to the current day at the next change, and a lease document's date may take its place (fillLease);
//   · '': somebody typed the date or emptied the box. That is their answer, and nothing here changes it, but for a box
//     they left blank when its card next comes on screen;
//   · none at all: nobody has answered the box — a form from before these defaults, or a card's Clear. It is filled at the
//     next change while its card is on screen.
// All of it runs in a change somebody makes (staffChange, through useAutosave's `set`), never on opening a form, so viewing
// writes nothing; never on a view link, which has no `day` and takes no change; and only while the form is New, so a
// Pending or Confirmed form's dates stay as they are, which the server holds to as well (saveData). A file of its own, free
// of React, so that server/src/scripts/testPropertyFormDates.ts can run these rules.
export type DateRule = { day: string; isNew: boolean };

const blank = (v: any): boolean => !String(v ?? '').trim();
/** The form with the day as the default of each box asked for; the form as it was when neither is. */
const withDay = (d: any, day: string, agreement: boolean, poa: boolean): any => {
  const filled = (box: any): any => ({ ...(box || {}), date: day, dateDefault: day });
  return agreement || poa
    ? { ...d, ...(agreement ? { agreement: filled(d?.agreement) } : {}), ...(poa ? { poa: filled(d?.poa) } : {}) }
    : d;
};

/** A default still showing goes on to the current day. */
export const rollDates = (d: any, rule: DateRule): any => {
  if (!rule.isNew || !rule.day) return d;
  const behind = (box: any): boolean => !blank(box?.dateDefault) && box.date === box.dateDefault && box.dateDefault !== rule.day;
  return withDay(d, rule.day, behind(d?.agreement), behind(d?.poa));
};

/** After a change, from form `was` to form `next`: a blank box on screen is filled once its card has just come on screen,
 *  or while nobody has answered it. `agreementShows` = the form shows the Agreement date (the Agreement card, or the
 *  Lease card's copy of the box); the POA's shows while 'Are we doing a POA for the buyer?' is Yes. */
export const fillDates = (was: any, next: any, rule: DateRule, agreementShows: (d: any) => boolean): any => {
  if (!rule.isNew || !rule.day) return next;
  const wanted = (box: any, shows: boolean, showed: boolean): boolean => shows && blank(box?.date) && (!showed || box?.dateDefault === undefined);
  return withDay(next, rule.day,
    wanted(next?.agreement, agreementShows(next), agreementShows(was)),
    wanted(next?.poa, next?.doPoa === 'Yes', was?.doPoa === 'Yes'));
};

/** One change `fn` to the form. A default moves on first, so that what the change works out from the date (a POA's Age,
 *  on ageDay) is worked out on the day the form will hold; then the change; then any blank box its card now asks for. */
export const staffChange = (d: any, fn: (d: any) => any, rule: DateRule, agreementShows: (d: any) => boolean): any => {
  const was = rollDates(d, rule);
  return fillDates(was, fn(was), rule, agreementShows);
};
